import React, { useEffect, useState, type Key } from 'react';
import { Row, Col, Button, message, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Entity } from 'store/ducks/InternalControls/types';
import { RootState } from 'store/configureStore';
import { internalControlsOp } from 'store/ducks/InternalControls';
import { AllocationConfig } from 'store/ducks/Global/types';
import Helpers from 'utils/Helpers';
import WhoAmILayout from 'components/Layouts/WhoAmILayout';
import CustomSelect from 'components/CustomSelect';
import ApiService from 'utils/ApiCalls';
import * as Styled from '../WhoAmI.styled';

const WhoAmIAllocationUsers: React.FC = () => {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const { scheme, lang } = useSelector((state: RootState) => ({
    scheme: state.global.scheme,
    lang: state.global.lang
  }));

  const [selectedPerson, setSelectedPerson] = useState<number | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [targets, setTargets] = useState<Record<string, Key[]>>({});
  const [initialTargets, setInitialTargets] = useState<Record<string, Key[]>>(
    {}
  );

  const [savePopup, setSavePopup] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [groups, setGroups] = useState<Entity[]>([]);
  const [divisions, setDivisions] = useState<Entity[]>([]);
  const [regions, setRegions] = useState<Entity[]>([]);
  const [legalEntities, setLegalEntities] = useState<Entity[]>([]);
  const [businessUnits, setBusinessUnits] = useState<Entity[]>([]);

  const loadInitialData = async () => {
    try {
      const [
        usersResponse,
        groupsResponse,
        divisionsResponse,
        regionsResponse,
        legalEntitiesResponse,
        businessUnitsResponse
      ] = await Promise.all([
        ApiService.getAllActiveUsers(),
        ApiService.getAllGroup(),
        ApiService.getAllDivision(),
        ApiService.getAllRegion(),
        ApiService.getAllLegalEntities(),
        ApiService.getAllBusinessUnites()
      ]);
      setUsers(
        usersResponse.data.map((user: any) => ({
          label: `${user.first_name} ${user.last_name}`,
          value: user.id
        }))
      );
      setGroups(
        groupsResponse.data.data.map((item: any) => ({
          id: item.id,
          name: item.name
        }))
      );
      setDivisions(
        divisionsResponse.data.data.map((item: any) => ({
          id: item.id,
          name: item.name
        }))
      );
      setRegions(
        regionsResponse.data.data.map((item: any) => ({
          id: item.id,
          name: item.name
        }))
      );
      setLegalEntities(
        legalEntitiesResponse.data.data.map((item: any) => ({
          id: item.id,
          name: item.name
        }))
      );
      setBusinessUnits(
        businessUnitsResponse.data.data.map((item: any) => ({
          id: item.id,
          name: item.name
        }))
      );
    } catch (error) {
      message.error('Failed to load allocation data');
    }
  };

  const loadUserAllocations = async (userId: number) => {
    try {
      const [
        groupsRes,
        divisionsRes,
        regionsRes,
        legalEntitiesRes,
        businessUnitsRes
      ] = await Promise.all([
        ApiService.getgroupsByUser(userId),
        ApiService.getDivisionsByUser(userId),
        ApiService.getRegionsByUser(userId),
        ApiService.getLegalEntitiesByUser(userId),
        ApiService.getBusinessUnitsByUser(userId)
      ]);
      const mapped = {
        group: groupsRes.data.data.map((x: any) => String(x.id)),

        division: divisionsRes.data.data.map((x: any) => String(x.id)),

        region: regionsRes.data.data.map((x: any) => String(x.id)),

        legal_entity: legalEntitiesRes.data.data.map((x: any) => String(x.id)),

        business_unit: businessUnitsRes.data.data.map((x: any) => String(x.id))
      };
      setTargets(mapped);
      setInitialTargets(mapped);
    } catch (error) {
      console.error(error);
      message.error('Failed to load user allocations');
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [lang]);

  const configs: AllocationConfig[] = [
    {
      id: 'group',
      title: t('ALLOCATE_GROUPS_TO_PERSON'),
      options: groups
    },
    {
      id: 'division',
      title: t('ALLOCATE_DIVISIONS_TO_PERSON'),
      options: divisions
    },
    {
      id: 'region',
      title: t('ALLOCATE_REGIONS_TO_PERSON'),
      options: regions
    },
    {
      id: 'legal_entity',
      title: t('ALLOCATE_LEGAL_ENTITIES_TO_PERSON'),
      options: legalEntities
    },
    {
      id: 'business_unit',
      title: t('ALLOCATE_BUSINESS_UNITS_TO_PERSON'),
      options: businessUnits
    }
  ];

  const hasChanges = (type: string) => {
    return !Helpers.areKeysEqual(
      targets[type] || [],
      initialTargets[type] || []
    );
  };

  const isBlocked = (type: string) => {
    return !selectedPerson || !hasChanges(type);
  };

  const handleSave = async (type: string) => {
    if (!selectedPerson) {
      message.error('Please select person');
      return;
    }

    try {
      setLoading((prev) => ({
        ...prev,
        [type]: true
      }));

      const ids = (targets[type] || []).map(Number);

      switch (type) {
        case 'group':
          await ApiService.mapGroupsToPerson(selectedPerson, ids);
          break;
        case 'division':
          await ApiService.mapDivisionsToPerson(selectedPerson, ids);
          break;
        case 'region':
          await ApiService.mapRegionsToPerson(selectedPerson, ids);
          break;
        case 'legal_entity':
          await ApiService.mapLegalEntitiesToPerson(selectedPerson, ids);
          break;
        case 'business_unit':
          await ApiService.mapBusinessUnitsToPerson(selectedPerson, ids);
          break;
      }
      setInitialTargets((prev) => ({
        ...prev,
        [type]: targets[type]
      }));
      await loadUserAllocations(selectedPerson);

      message.success('Allocation saved successfully');
    } catch (error: any) {
      message.error(error?.message || 'Save failed');
    } finally {
      setLoading((prev) => ({
        ...prev,
        [type]: false
      }));
    }
  };

  useEffect(() => {
    const changed = configs.some((c) => hasChanges(c.id));
    dispatch(internalControlsOp.setHasChanges(changed));
  }, [targets, initialTargets, dispatch]);

  return (
    <WhoAmILayout>
      <Styled.ContentWrapper>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <CustomSelect
              placeholder='Select Person'
              value={selectedPerson}
              onChange={async (value: number) => {
                setSelectedPerson(value);
                await loadUserAllocations(value);
              }}
              options={users}
            />
          </Col>
          {configs.map((c) => {
            return (
              <Col xs={24} md={12} key={c.id}>
                <Styled.TransferCard
                  scheme={scheme}
                  title={c.title}
                  extra={
                    <Popover
                      content={
                        !selectedPerson
                          ? 'Please select person first'
                          : 'No changes'
                      }
                      open={savePopup[c.id]}
                    >
                      <Button
                        type='primary'
                        loading={loading[c.id]}
                        disabled={isBlocked(c.id)}
                        onClick={() => {
                          if (isBlocked(c.id)) {
                            setSavePopup((prev) => ({
                              ...prev,
                              [c.id]: true
                            }));
                            setTimeout(() => {
                              setSavePopup((prev) => ({
                                ...prev,
                                [c.id]: false
                              }));
                            }, 1500);
                            return;
                          }
                          handleSave(c.id);
                        }}
                      >
                        {t('SAVE')}
                      </Button>
                    </Popover>
                  }
                >
                  <Styled.EqualTransfer
                    className='equal-transfer'
                    dataSource={c.options.map((item) => ({
                      key: String(item.id),
                      title: item.name
                    }))}
                    targetKeys={targets[c.id] || []}
                    onChange={(keys) => {
                      setTargets((prev) => ({
                        ...prev,
                        [c.id]: keys
                      }));
                    }}
                    render={(item) => <div>{String(item.title || '')}</div>}
                    showSearch
                    disabled={!selectedPerson}
                    listStyle={{
                      height: 280
                    }}
                  />
                </Styled.TransferCard>
              </Col>
            );
          })}
        </Row>
      </Styled.ContentWrapper>
    </WhoAmILayout>
  );
};

export default WhoAmIAllocationUsers;
